import { Loading } from "../../app/Status";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router";
import { ACTIVE_STATUSES, PRIO, PRIORITIES, type MeterRoundDto, type ObligationListRow } from "@klantpunt/shared";
import { api } from "../../lib/api";
import { hasModule, useMe } from "../../lib/auth";
import { useMediaQuery } from "../../lib/useMediaQuery";
import { useRealtime } from "../../lib/realtime";
import { buildingPath } from "../../app/navItems";
import {
  IconAssets,
  IconBoard,
  IconChevronRight,
  IconEquipment,
  IconInspections,
  IconIssues,
  IconMeter,
} from "../../app/icons";
import { countByPriority } from "../issues/filters";
import { useIssues } from "../issues/useIssues";
import { useTasks } from "../tasks/useTasks";
import { useBuilding } from "./useBuilding";

/** Telt rustig op naar het getal (ook bij een wijziging); bij "minder beweging" meteen het getal. */
export function useCountUp(target: number, ms = 700) {
  const still = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [shown, setShown] = useState(still ? target : 0);
  const from = useRef(shown);
  useEffect(() => {
    if (still) {
      from.current = target;
      setShown(target);
      return;
    }
    const start = performance.now();
    const begin = from.current;
    let raf = 0;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / ms);
      const eased = 1 - (1 - t) ** 3;
      const v = Math.round(begin + (target - begin) * eased);
      from.current = v;
      setShown(v);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, ms, still]);
  return shown;
}

/** Getal dat optelt; schermlezers krijgen alleen het eindgetal. */
function Count({ value }: { value: number }) {
  const shown = useCountUp(value);
  return (
    <span className="num">
      <span aria-hidden="true">{shown}</span>
      <span className="sr-only">{value}</span>
    </span>
  );
}

function Tile({
  to,
  icon,
  label,
  value,
  sub,
  tone,
  children,
}: {
  to: string;
  icon: ReactNode;
  label: string;
  value: number | null;
  sub?: ReactNode;
  tone?: "danger" | "warn" | "ok";
  children?: ReactNode;
}) {
  return (
    <Link to={to} className={`dtile ${tone ? `tone-${tone}` : ""}`}>
      <span className="dtile-head">
        <span className="dtile-ico" aria-hidden="true">
          {icon}
        </span>
        <span className="dtile-label">{label}</span>
        <IconChevronRight size={18} aria-hidden="true" className="dtile-go" />
      </span>
      <span className="dtile-value">{value == null ? <span className="muted">–</span> : <Count value={value} />}</span>
      {sub && <span className="dtile-sub muted small">{sub}</span>}
      {children}
    </Link>
  );
}

interface MeterBuildingRow {
  building: { id: string };
  meters: number;
  openRound: MeterRoundDto | null;
}

/** Overzicht van één gebouw: open bevindingen per prioriteit, taken, keuringen, assets en de meterronde. */
export function BuildingOverview() {
  const { id = "" } = useParams();
  const { data: me } = useMe();
  const seeIssues = hasModule(me, "issues");
  const seeAssets = hasModule(me, "assets");
  const seeMeters = hasModule(me, "meters");
  const { data: b, isPending, error } = useBuilding(id);
  useRealtime(id);
  const issues = useIssues(id, seeIssues);
  const tasks = useTasks(id, seeIssues);
  const obligations = useQuery({
    queryKey: ["obligation-list", id],
    queryFn: () => api<ObligationListRow[]>(`/buildings/${id}/obligations`),
    enabled: seeAssets,
  });
  const meters = useQuery({
    queryKey: ["meter-buildings"],
    queryFn: () => api<MeterBuildingRow[]>("/meters/buildings"),
    enabled: seeMeters,
  });

  if (isPending) return <Loading page />;
  if (error || !b) return <div className="center">Dit gebouw bestaat niet (meer) of je hebt er geen toegang toe.</div>;

  const sec = (s: Parameters<typeof buildingPath>[1]) => buildingPath(id, s);
  const all = issues.data ?? [];
  const counts = countByPriority(all);
  const active = all.filter((i) => ACTIVE_STATUSES.has(i.status)).length;
  const urgent = counts.P1 + counts.P2;
  const openTasks = (tasks.data ?? []).filter((t) => t.status !== "klaar");
  const busyTasks = openTasks.filter((t) => t.status === "bezig").length;
  const obl = obligations.data ?? [];
  const overdue = obl.filter((o) => o.status === "overdue").length;
  const soon = obl.filter((o) => o.status === "soon").length;
  const equipment = b.assets.filter((a) => a.assetKind === "equipment").length;
  // Met de module Meterstanden hebben meters een eigen lijst; anders tellen ze bij Assets.
  const meterAssets = seeMeters ? b.assets.filter((a) => a.assetKind === "meter").length : 0;
  const meterRow = meters.data?.find((r) => r.building.id === id);
  const round = meterRow?.openRound;
  const maxPrio = Math.max(1, ...PRIORITIES.map((p) => counts[p]));

  return (
    <div className="page overview">
      <header className="pagehead">
        <p className="label">
          <span className="code">{b.code}</span>
          {b.address ? ` · ${[b.address, b.city].filter(Boolean).join(", ")}` : ""}
        </p>
        <div className="titlerow">
          <h1 className="dot">{b.name}</h1>
        </div>
      </header>

      <div className="dgrid">
        {seeIssues && (
          <Tile
            to={sec("bevindingen")}
            icon={<IconIssues size={20} />}
            label="Open bevindingen"
            value={issues.data ? active : null}
            tone={urgent ? "danger" : undefined}
            sub={urgent ? `${urgent} met spoed of hoge prioriteit` : "Geen spoed"}
          >
            <span className="dprio">
              {PRIORITIES.map((p) => (
                <span key={p} className="dprio-row" title={`${PRIO[p].l}: ${PRIO[p].t}`}>
                  <span className="dprio-key">
                    <i className={`prio-${p}`} aria-hidden="true" /> {p}
                  </span>
                  <span className="dprio-bar" aria-hidden="true">
                    <span className={`prio-${p}`} style={{ width: `${(counts[p] / maxPrio) * 100}%` }} />
                  </span>
                  <Count value={counts[p]} />
                </span>
              ))}
            </span>
          </Tile>
        )}
        {seeIssues && (
          <Tile
            to={sec("taken")}
            icon={<IconBoard size={20} />}
            label="Open taken"
            value={tasks.data ? openTasks.length : null}
            sub={busyTasks ? `${busyTasks} in uitvoering` : "Niets in uitvoering"}
          />
        )}
        {seeAssets && (
          <Tile
            to={sec("keuringen")}
            icon={<IconInspections size={20} />}
            label="Onderhoud en keuringen verlopen"
            value={obligations.data ? overdue : null}
            tone={overdue ? "danger" : soon ? "warn" : obligations.data ? "ok" : undefined}
            sub={soon ? `${soon} binnenkort` : overdue ? "Niets binnenkort" : "Alles op orde"}
          />
        )}
        {seeAssets && (
          <Tile
            to={sec("assets")}
            icon={<IconAssets size={20} />}
            label="Assets"
            value={b.assets.length - equipment - meterAssets}
            sub={seeMeters ? "Installaties en bouwkundig" : "Installaties, bouwkundig en meters"}
          />
        )}
        {seeAssets && (
          <Tile
            to={sec("arbeidsmiddelen")}
            icon={<IconEquipment size={20} />}
            label="Arbeidsmiddelen"
            value={equipment}
            sub="Ladders, gereedschap en apparaten"
          />
        )}
        {seeMeters && (
          <Tile
            to={seeAssets ? sec("meters") : round ? `/meterstanden/ronde/${round.id}` : "/meterstanden"}
            icon={<IconMeter size={20} />}
            label={round ? round.name : "Meters"}
            value={round ? round.done : meters.data ? (meterRow?.meters ?? 0) : null}
            sub={round ? `van ${round.total} opgenomen` : "Geen lopende ronde"}
          >
            {round && (
              <span className="dbar" aria-hidden="true">
                <span style={{ width: `${round.total ? (round.done / round.total) * 100 : 0}%` }} />
              </span>
            )}
          </Tile>
        )}
      </div>
    </div>
  );
}
